import { Button, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { useTranslation } from "react-i18next";
import ExportedAlertLineReport from "./ExportedAlertLineReport/ExportedAlertLineReport";
import { AlertLineReportDTO } from "../../models/alert-line-report/alert-line-report-dto";
import { AlertLineReportNoteDTO } from "../../models/alert-line-report-note/alert-line-report-note-dto";
import { AlertLineReportNoteFilter } from "../../models/alert-line-report-note/alert-line-report-note-filter";
import { customFileIconStyles } from "../../reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import { AlertLineReportNoteApiService } from "../../services/alert-line/alert-line-report-note-api.service";
import { exportContentToPDF } from "../../utils/export-utils/export-content-as-pdf";
import { FileIconButton } from "../AttachedFiles/styles";

interface Props {
  alertLineReport: AlertLineReportDTO;
  setExportLoading: (loading: boolean) => void;
  useFileIcon?: boolean;
  customButtonStyle?: React.CSSProperties;
  asReviewer?: boolean;
}

interface State {
  loadingNotes: boolean;
  allNotes: AlertLineReportNoteDTO[];
  notesLoaded: boolean;
}

const ExportAlertLineReport = ({
  alertLineReport,
  setExportLoading,
  useFileIcon,
  customButtonStyle,
  asReviewer,
}: Props) => {
  const tRP = "components.export_alert_line_report";
  const { t } = useTranslation();
  const theme = useTheme();

  const [state, setState] = useState<State>({
    allNotes: [],
    loadingNotes: false,
    notesLoaded: false,
  });
  //#region load notes
  const loadAllNotes = useCallback(async () => {
    setState((state) => ({ ...state, loadingNotes: true }));
    try {
      const page = {
        pageNumber: 0,
        pageSize: 100000,
      };

      const apiService = new AlertLineReportNoteApiService();
      if (!asReviewer) {
        apiService.useLoadItemsPath("/GetPublicAlertLineReportNotes");
      }

      const response = await apiService.loadItems(
        {
          alertLineReportId: alertLineReport.id,
        } as AlertLineReportNoteFilter,
        "",
        undefined,
        page,
        undefined,
        60,
        undefined
      );
      if (!response.error) {
        setState((state) => ({
          ...state,
          allNotes: [...response.items],
          notesLoaded: true,
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loadingNotes: false }));
    }
  }, [alertLineReport.id, asReviewer]);
  //#endregion

  //#region export to pdf
  const exportToPDF = useCallback(async () => {
    const contentToExport = () => {
      return (
        <>
          <ExportedAlertLineReport
            alertLineReport={alertLineReport}
            allNotes={state.allNotes}
          />
        </>
      );
    };

    await exportContentToPDF(
      await contentToExport(),
      setExportLoading,
      `${t(`${tRP}.report`)}_${alertLineReport.key}`,
      true
    );
  }, [alertLineReport, setExportLoading, state.allNotes, t]);
  //#endregion

  //#region handle export
  const handleExport = useCallback(async () => {
    try {
      await loadAllNotes();
    } catch (error) {
      console.error("Error loading notes for export:", error);
    }
  }, [loadAllNotes]);
  //#endregion

  useEffect(() => {
    if (state.notesLoaded) exportToPDF();
  }, [state.notesLoaded]);

  return (
    <>
      {useFileIcon ? (
        <FileIconButton
          onClick={handleExport}
          title={`${t(`${tRP}.export_report`)}`}
        >
          <FileIcon
            extension="pdf"
            {...customFileIconStyles["pdf" as DefaultExtensionType]}
            radius={3}
          />
        </FileIconButton>
      ) : (
        <Button
          title={`${t(`${tRP}.export_report`)}`}
          onClick={handleExport}
          variant="contained"
          style={{ textTransform: "none", backgroundColor: theme.palette.success.main, ...customButtonStyle }}
        >
          {`${t(`${tRP}.export`)}`}
        </Button>
      )}
    </>
  );
};
export default ExportAlertLineReport;
